import { TimeFilterSlug } from '@store/modules/ui/shared/timeFilter/utils';
import { SelectionSourceScreen as SelectionSourceScreenType } from '@superbet-group/product.analytics';
import { SelectionSourceScreen } from '@superbet-group/web.types';
import { Route } from 'vue-router';

/*
    we decided against global event name enums since each federation will be in charge
    of their own event names for their own routes - no need to import/export event names
    that will never be used anywhere else - these here are shell only
*/
export const AnalyticsScreenNames = {
    home: 'route_home',
    sportOffer: 'sportOffer', // currently used to validate pre-match/live during route triggers
    preMatchSportOffer: 'route_prematch_sport_offer',
    liveSportOffer: 'route_live_sport_offer',
    resultsSport: 'route_sport_results',
    resultsLive: 'route_live_results',
    resultsVirtualShop: 'route_virtual_shop_results',
    resultsVirtual: 'route_virtual_results',
    news: 'route_news',
    newsArticleDetails: 'route_news_article_details',
    wiki: 'route_wiki',
    registration: 'route_registration',
    passwordRecovery: 'route_password_recovery',
    forgotPassword: 'route_forgot_password',
    accountRecovery: 'route_account_recovery',
    resendActivationEmail: 'route_resend_activation_email',
    activateAccount: 'route_activate_account',
    idCardVerificationCaptive: 'route_id_card_verification_captive',
    profile: 'route_profile',
    profileReferAFriend: 'route_profile_refer_a_friend',
    profileMessages: 'route_profile_messages',
    profileHistory: 'route_profile_history',
    profileDeposit: 'route_profile_deposit',
    profileWithdrawal: 'route_profile_withdrawal',
    profileBonus: 'route_profile_bonus',
    profileSettings: 'route_profile_settings',
    profilePasswordUpdate: 'route_profile_password_update',
    profileDocumentUpload: 'route_profile_document_upload',
    profileIdentityVerification: 'route_profile_identity_verification',
    promotionsAndBonuses: 'route_promotions_and_bonuses',
    continueRegistration: 'route_continue_registration',
    competitionDetailsOutrights: 'competition_details_outrights',
    competitionDetailsOffer: 'competition_details_offer',
    competitionDetailsStandings: 'competition_details_standings',
    prematchCalendar: 'prematch_sport_calendar',
    prematchCompetitions: 'prematch_sport_competitions',
    liveOffer: 'live_sport_offer',
    helpAndSupport: 'route_help_and_support',
    changePassword: 'route_change_password',
    notifications: 'route_notifications',
    myAccount: 'my_account',
    marketingConsent: 'route_marketing_consent',
    socialProfile: 'route_social_profile',
    editSocialProfile: 'route_social_profile_edit',
    blockedUsers: 'blocked_users_list',
    chat: 'route_chat',
    superAdvantagePages: 'super_advantage_pages',
    superAdvantagePagesSport: 'super_advantage_pages_sport',
    topWinners: 'top_winners',
    loyaltySupercoins: 'loyalty_supercoins',
} as const;

export const AnalyticsModalNames = {
    login: 'login_modal',
    forgotPassword: 'forgot_password_modal',
    resetPassword: 'reset_password_modal',
    accountLocked: 'account_locked_modal',
    accountCreated: 'account_created',
    accountActivationSuccess: 'account_activation_success',
    accountActivationError: 'account_activation_error',
} as const;

export type AnalyticsScreenNames = typeof AnalyticsScreenNames[keyof typeof AnalyticsScreenNames];

export const AnalyticsOpenEventNames = {
    newsArticleDetails: 'news_article_details',
} as const;

export type AnalyticsOpenEventNames = typeof AnalyticsOpenEventNames[keyof typeof AnalyticsOpenEventNames];

export const AnalyticsEventTypes = {
    sessionScreenVisit: 'session_screen_visit',
    sessionUser: 'session_user',
    contentOpen: 'content_open',
    accountOpen: 'account_open',
    betslipAddResult: 'betslip_add_result',
    betslipSubmitResult: 'betslip_submit_result',
    betslipSubmitIntent: 'betslip_submit_intent',
    multimediaRequestResult: 'multimedia_request_result',
    offerOpen: 'offer_open',
    statsOpen: 'stats_open',
    contentLanderTransition: 'content_lander_transition',
    registrationRequestResult: 'registration_request_result',
    sectionViewportVisibility: 'section_viewport_visibility',
    carouselSlide: 'carousel_slide',
} as const;

export type AnalyticsEventTypes = typeof AnalyticsEventTypes[keyof typeof AnalyticsEventTypes];

export const AnalyticsProtoEvents = {
    walletRegistrationStep1: 'walletRegistrationStep_1',
    onboardingApproval: 'onboardingApproval',
    sessionStart: 'sessionSessionStart',
    sessionEnd: 'sessionSessionEnd',
    sessionType: 'sessionSessionType',
    sessionUser: 'sessionSessionUser',
    sessionPushNotifications: 'sessionSessionPushNotifications',
    sessionLocation: 'sessionSessionLocation',
    sessionScreenVisit: 'sessionSessionScreenVisit',
    screenOpenStatsOpen: 'screenOpenStatsOpen',
    screenOpenSocialOpen: 'screenOpenSocialOpen',
    screenOpenContentOpen: 'screenOpenContentOpen',
    screenOpenAccountOpen: 'screenOpenAccountOpen',
    screenOpenOfferOpen: 'screenOpenOfferOpen',
    click: 'click',
    betslipAddResult: 'betSlipAddResult',
    betslipSubmitResult: 'betslipSubmitResult',
    betslipSubmitIntent: 'betslipSubmitIntent',
    multimediaRequestResult: 'multimediaRequestResult',
    contentLanderTransition: 'contentLanderTransition',
    registrationRequestResult: 'registrationRequestResult',
    sectionViewportVisibility: 'sectionViewportVisibility',
    carouselSlide: 'carouselSlide',
} as const;

export type AnalyticsProtoEvents = typeof AnalyticsProtoEvents[keyof typeof AnalyticsProtoEvents];

/*
    Since "live" is determined from the route params, and there's multiple routes that can have "live" as a param,
    we need to check those as well as the name itself before submitting analytics
*/
function validateLiveSportOffer(route: Route) {
    if (route.meta?.superAnalytics?.pageName === AnalyticsScreenNames.sportOffer) {
        return route.params?.timeFilter === TimeFilterSlug.Live
            ? AnalyticsScreenNames.liveSportOffer
            : AnalyticsScreenNames.preMatchSportOffer;
    } else {
        return route.meta?.superAnalytics?.pageName;
    }
}

/*
    not complicating at the moment with some generic route check since live/prematch is the only fiddly analytics route.
    If we run into similar issues with other route names and router behaviour, we'll refactor this to cover such cases.
    Kinda difficult to write generic code for cases that don't exist :)
 */
export function prepareAnalyticsScreenName(to: Route, from: Route) {
    const previousScreenName = validateLiveSportOffer(from);
    const currentScreenName = validateLiveSportOffer(to);

    // not sending events for routes that reuse same components but different filter params
    return previousScreenName !== currentScreenName ? currentScreenName : undefined;
}

export const BetSlipEventStatusType = {
    PREMATCH: 1,
    LIVE: 2,
    POSTMATCH: 3,
    NOT_LIVE: 4,
} as const;

export const BetslipResultType = {
    FAILED: 1,
    SUCCESSFUL: 2,
} as const;

export const BetslipSubmitMethodType = {
    ONLINE: 1,
    PREPARED: 2,
} as const;

export const TicketSystemType = {
    SYSTEM: 1,
    NORMAL: 2,
} as const;

export const MultimediaContentType = {
    STREAM: 1,
    VISUALIZATION: 2,
} as const;

export const MultimediaRequestLocation = {
    NAVIGATION: 4,
} as const;

export const Prefix = {
    AXILIS: 'ax',
    BETRADAR: 'br',
} as const;

type TPrefix = typeof Prefix[keyof typeof Prefix];

export const ParamTypes = {
    SPORT: 'sport',
    COMPETITION: 'competition',
    MATCH: 'match',
    TEAM: 'team',
    CATEGORY: 'category',
    TOURNAMENT: 'tournament',
} as const;

type TParamTypes = typeof ParamTypes[keyof typeof ParamTypes];

export const getParamWithPrefix = (prefix: TPrefix, type: TParamTypes, value?: string | number): string | undefined => {
    if (value && typeof value === 'number') {
        return `${prefix}:${type}:${value}`;
    }

    return value ? String(value) : undefined;
};

export const SectionVisibilityAnalyticNames = {
    SportsCalendarPopularAccumulators: 'SportsCalendarPopularAccumulators',
    HomeInPlay: 'HomeInPlay',
};

export const SelectionSourceScreenMap = {
    [SelectionSourceScreen.Unknown]: SelectionSourceScreenType.SOURCE_SCREEN_UNSPECIFIED,
    [SelectionSourceScreen.MatchDetailsOffer]: SelectionSourceScreenType.MATCH_DETAILS_OFFER,
    [SelectionSourceScreen.MatchDetailsH2HPerformance]: SelectionSourceScreenType.MATCH_DETAILS_H2H_PERFORMANCE,
    [SelectionSourceScreen.MatchDetailsPointByPoint]: SelectionSourceScreenType.MATCH_DETAILS_POINT_BY_POINT,
    [SelectionSourceScreen.CompetitionDetailsOffer]: SelectionSourceScreenType.COMPETITION_DETAILS_OFFER,
    [SelectionSourceScreen.CompetitionDetailsOutrights]: SelectionSourceScreenType.COMPETITION_DETAILS_OUTRIGHTS,
    [SelectionSourceScreen.TeamDetailsOverviewNextMatch]: SelectionSourceScreenType.TEAM_DETAILS_OVERVIEW_NEXT_MATCH,
    [SelectionSourceScreen.PlayerDetailsOverviewNextMatch]: SelectionSourceScreenType.PLAYER_DETAILS_OVERVIEW_NEXT_MATCH,
    [SelectionSourceScreen.TicketDetailsBets]: SelectionSourceScreenType.TICKET_DETAILS_BETS,
    [SelectionSourceScreen.TicketDetailsWidget]: SelectionSourceScreenType.TICKET_DETAILS_WIDGET,
    [SelectionSourceScreen.TicketDetailsSocial]: SelectionSourceScreenType.TICKET_DETAILS_SOCIAL,
    [SelectionSourceScreen.TicketDetailsTicketCopy]: SelectionSourceScreenType.TICKET_DETAILS_TICKET_COPY,
    [SelectionSourceScreen.TicketDetailsSelectionCopy]: SelectionSourceScreenType.TICKET_DETAILS_SELECTION_COPY,
    [SelectionSourceScreen.TicketDetailsTicketRecreate]: SelectionSourceScreenType.TICKET_DETAILS_TICKET_RECREATE,
    [SelectionSourceScreen.TicketMyBetsDesktopOpen]: SelectionSourceScreenType.TICKET_MY_BETS_DESKTOP_OPEN,
    [SelectionSourceScreen.TicketMyBetsDesktopSettled]: SelectionSourceScreenType.TICKET_MY_BETS_DESKTOP_SETTLED,
    [SelectionSourceScreen.TicketMyBetsDesktopPrepared]: SelectionSourceScreenType.TICKET_MY_BETS_DESKTOP_PREPARED,
    [SelectionSourceScreen.HomeSuperLive]: SelectionSourceScreenType.HOME_SUPER_LIVE,
    [SelectionSourceScreen.HomeSuperOdds]: SelectionSourceScreenType.HOME_SUPER_ODDS,
    [SelectionSourceScreen.HomeInPlay]: SelectionSourceScreenType.HOME_IN_PLAY,
    [SelectionSourceScreen.HomeTopTen]: SelectionSourceScreenType.HOME_TOP_TEN,
    [SelectionSourceScreen.HomeSuperExtra]: SelectionSourceScreenType.HOME_SUPER_EXTRA,
    [SelectionSourceScreen.HomePopularSuperbets]: SelectionSourceScreenType.HOME_POPULAR_SUPERBETS,
    [SelectionSourceScreen.HomeTopPriceBoost]: SelectionSourceScreenType.HOME_PRICE_BOOST,
    [SelectionSourceScreen.LiveSuperLive]: SelectionSourceScreenType.LIVE_SUPER_LIVE,
    [SelectionSourceScreen.LiveCompetitions]: SelectionSourceScreenType.LIVE_COMPETITIONS,
    [SelectionSourceScreen.SportsCalendarPopularEvents]: SelectionSourceScreenType.SPORTS_CALENDAR_POPULAR_EVENTS,
    [SelectionSourceScreen.SportsCalendarPopularSuperbets]: SelectionSourceScreenType.SPORTS_CALENDAR_POPULAR_SUPERBETS,
    [SelectionSourceScreen.SportsCalendarCompetitions]: SelectionSourceScreenType.SPORTS_CALENDAR_COMPETITIONS,
    [SelectionSourceScreen.SportsCalendarsDailyTicket]: SelectionSourceScreenType.SPORTS_CALENDARS_DAILY_TICKET,
    [SelectionSourceScreen.SportsFeaturedPopularEvents]: SelectionSourceScreenType.SPORTS_FEATURED_POPULAR_EVENTS,
    [SelectionSourceScreen.SportsFeaturedSuperOdds]: SelectionSourceScreenType.SPORTS_FEATURED_SUPER_ODDS,
    [SelectionSourceScreen.SportsFeaturedPopularSuperbets]: SelectionSourceScreenType.SPORTS_FEATURED_POPULAR_SUPERBETS,
    [SelectionSourceScreen.SportsCalendarSuperOdds]: SelectionSourceScreenType.SPORTS_CALENDAR_SUPER_ODDS,
    [SelectionSourceScreen.SportsCalendarTopTen]: SelectionSourceScreenType.SPORTS_CALENDAR_TOP_TEN,
    [SelectionSourceScreen.SportsCalendarSuperExtra]: SelectionSourceScreenType.SPORTS_CALENDAR_SUPER_EXTRA,
    [SelectionSourceScreen.SportsCalendarTopPlayers]: SelectionSourceScreenType.SPORTS_CALENDAR_TOP_PLAYERS,
    [SelectionSourceScreen.SportsSuperAdvantagePages]: SelectionSourceScreenType.SPORTS_SUPER_ADVANTAGE_PAGES,
    [SelectionSourceScreen.SportsCalendarPopularAccumulators]: SelectionSourceScreenType.SPORTS_CALENDAR_POPULAR_ACCUMULATORS,
    [SelectionSourceScreen.SportsCalendarPopularAccumulatorsAddMoreSelections]: SelectionSourceScreenType.SPORTS_CALENDAR_POPULAR_ACCUMULATORS_ADD_MORE_SELECTIONS,
    [SelectionSourceScreen.SportsExperimentalPopularSuperbets]: SelectionSourceScreenType.SPORTS_EXPERIMENTAL_POPULAR_SUPERBETS,
    [SelectionSourceScreen.SearchUpcoming]: SelectionSourceScreenType.SEARCH_UPCOMING,
    [SelectionSourceScreen.BettingRoomOffer]: SelectionSourceScreenType.BETTING_ROOM_OFFER,
    [SelectionSourceScreen.SpecialsDetails]: SelectionSourceScreenType.SPECIALS_DETAILS,
    [SelectionSourceScreen.NewsArticleArticleBody]: SelectionSourceScreenType.NEWS_ARTICLE_ARTICLE_BODY,
    [SelectionSourceScreen.NewsArticleRelatedMatch]: SelectionSourceScreenType.NEWS_ARTICLE_RELATED_MATCH,
    [SelectionSourceScreen.SuperSocialBetswipe]: SelectionSourceScreenType.SUPER_SOCIAL_BETSWIPE,
    [SelectionSourceScreen.SuperSocialFeedExplore]: SelectionSourceScreenType.SUPER_SOCIAL_FEED_EXPLORE,
    [SelectionSourceScreen.SuperSocialVideoContent]: SelectionSourceScreenType.SUPER_SOCIAL_VIDEO_CONTENT,
    [SelectionSourceScreen.UserAnalysisDetails]: SelectionSourceScreenType.USER_ANALYSIS_DETAILS,
    [SelectionSourceScreen.UserAnalysisLists]: SelectionSourceScreenType.USER_ANALYSIS_LISTS,
    [SelectionSourceScreen.UserAnalysisListTicketDetails]: SelectionSourceScreenType.USER_ANALYSIS_LIST_TICKET_DETAILS,
    [SelectionSourceScreen.UserAnalysisListSocialProfile]: SelectionSourceScreenType.USER_ANALYSIS_LIST_SOCIAL_PROFILE,
    [SelectionSourceScreen.UserAnalysisListPopularAnalyses]: SelectionSourceScreenType.USER_ANALYSIS_LIST_POPULAR_ANALYSES,
    [SelectionSourceScreen.UserAnalysisListMatchDetails]: SelectionSourceScreenType.USER_ANALYSIS_LIST_MATCH_DETAILS,
    [SelectionSourceScreen.UserAnalysisListSocialProfileTab]: SelectionSourceScreenType.USER_ANALYSIS_LIST_SOCIAL_PROFILE_TAB,
    [SelectionSourceScreen.UserAnalysisListCompetitionDetails]: SelectionSourceScreenType.USER_ANALYSIS_LIST_COMPETITION_DETAILS,
    [SelectionSourceScreen.BetCreator]: SelectionSourceScreenType.BET_CREATOR,
    [SelectionSourceScreen.MultiEventBetBuilder]: SelectionSourceScreenType.MULTI_EVENT_BET_BUILDER,
    [SelectionSourceScreen.TopWinners]: SelectionSourceScreenType.TOP_WINNERS,
    [SelectionSourceScreen.PriceBoostPages]: SelectionSourceScreenType.PRICE_BOOST_DEDICATED_PAGES,
} as const;

export type SelectionSourceScreenMap = typeof SelectionSourceScreenMap[keyof typeof SelectionSourceScreenMap];
